import { useState } from "react";
import { Link, createSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { EMPTY_FILTERS, PRICE_STEPS, filterVehicles, gbp, makeOfModel, makes, modelOptions, toParams, vehicles } from "@/lib/inventory";
import { EASE } from "@/lib/site";
import { SelectField } from "@/components/site/SelectField";

const BUDGETS = [3000, 5000, 8000];

export const HeroSearch = () => {
  const navigate = useNavigate();
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const filters = { ...EMPTY_FILTERS, make, model, maxPrice: Number(maxPrice) || 0 };
  const count = filterVehicles(vehicles, filters).length;

  const submit = (e) => {
    e.preventDefault();
    navigate({ pathname: "/used-cars", search: `?${createSearchParams(toParams(filters))}` });
  };

  return (
    <div className="relative z-20 -mt-20 lg:-mt-24">
      <div className="container-x">
        <motion.form
          onSubmit={submit}
          role="search"
          aria-label="Search used cars"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
          className="grid gap-4 rounded-lg border border-line bg-white p-5 shadow-[0_40px_80px_-40px_rgba(11,13,18,0.55)] sm:p-6 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end lg:p-7"
          data-testid="hero-search-form"
        >
          <div className="flex items-center justify-between gap-4 md:col-span-4">
            <h2 className="font-display text-lg font-extrabold">Find your next car</h2>
            <p className="text-sm text-steel">
              <span className="font-semibold text-ink">{vehicles.length}</span> cars in stock
            </p>
          </div>
          <SelectField
            id="hs-make"
            label="Make"
            value={make}
            onChange={(m) => {
              setMake(m);
              setModel("");
            }}
            options={makes.map((m) => ({ value: m, label: m }))}
            placeholder="Any make"
            testId="hero-search-make-select"
          />
          <SelectField
            id="hs-model"
            label="Model"
            value={model}
            onChange={(m) => {
              setModel(m);
              if (m && !make) setMake(makeOfModel(m));
            }}
            options={modelOptions(make)}
            placeholder="Any model"
            testId="hero-search-model-select"
          />
          <SelectField
            id="hs-price"
            label="Maximum price"
            value={maxPrice}
            onChange={setMaxPrice}
            options={PRICE_STEPS.map((p) => ({ value: String(p), label: `Up to ${gbp(p)}` }))}
            placeholder="No maximum"
            testId="hero-search-price-select"
          />
          <button type="submit" className="btn btn-primary w-full md:w-auto" data-testid="hero-search-submit-button">
            <Search aria-hidden="true" /> Search cars
            <span className="rounded-sm bg-white/15 px-1.5 py-0.5 text-xs tabular-nums" aria-label={`${count} matching cars`}>
              {count}
            </span>
          </button>
          <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4 md:col-span-4" data-testid="budget-shortcuts">
            <span className="mr-1 text-sm font-semibold text-steel">Quick budgets:</span>
            {BUDGETS.map((b) => (
              <Link
                key={b}
                to={`/used-cars?maxPrice=${b}&sort=price-desc`}
                className="chip"
                data-testid={`budget-under-${b / 1000}k-link`}
              >
                Under £{b / 1000}k <span className="text-xs opacity-70">({vehicles.filter((v) => v.price <= b).length})</span>
              </Link>
            ))}
          </div>
        </motion.form>
      </div>
    </div>
  );
};
